import type {
  CameraStream,
  JvcCamera,
  MinStockValues,
  Product,
  PromoProduct,
  StockData,
  Store,
  StoreActionLog,
  StoreCamera,
  StoreCameraConfigState,
  StoreCameraConfigSummary,
  UniqueArticle,
} from '@gozoki/api-types'
import { gozokiApi, gozokiApiWithAuth } from '../gozokiApi'

// Fetch all stores
export const fetchStores = async () => {
  const response = await gozokiApi.get<Store[]>('/stores')
  return response.data
}

export const fetchBoutiques = async () => {
  const response = await gozokiApi.get<Store[]>('/stores/boutiques')
  return response.data
}

export const fetchBackOfficeBoutiques = async () => {
  const response = await gozokiApiWithAuth.get<Store[]>('/stores', {
    params: { storeType: 'BOUTIQUE' },
  })
  return response.data
}

// Liste AUTHENTIFIÉE (admin) de TOUS les stores : `/stores` sans `storeType` renvoie, pour un admin,
// toutes les boutiques (tous statuts) + le store REMOTE. Labels complets (contrairement à l'endpoint
// RH available-stores). Le fetch public `fetchStores` (gozokiApi sans token) exclut REMOTE + non-online.
export const fetchStoresAuthed = async () => {
  const response = await gozokiApiWithAuth.get<Store[]>('/stores')
  return response.data
}

// Fetch all articles related to a store. Admin only (back office)
export const fetchStoreArticles = async (id: number) => {
  const response = await gozokiApiWithAuth.get<UniqueArticle[]>(`/stores/${id}/unique_articles`)
  return response.data
}

export const fetchStoreProducts = async (id: number) => {
  const response = await gozokiApi.get<Product[]>(`/stores/${id}/products`)
  return response.data
}

export const fetchStoreNewProducts = async (id: number, page?: number) => {
  if (page) {
    const response = await gozokiApi.get<{ products: Product[]; hasMorePage: boolean }>(
      `/stores/${id}/new-products/${page}`,
    )
    return { data: response.data.products, hasMorePage: response.data.hasMorePage }
  }
  const response = await gozokiApi.get<{ products: Product[]; hasMorePage: boolean }>(
    `/stores/${id}/new-products`,
  )
  return { data: response.data.products, hasMorePage: response.data.hasMorePage }
}

export const fetchAntiGaspi = async (id: number, page?: number) => {
  if (page) {
    const response = await gozokiApi.get<{ products: Product[]; hasMorePage: boolean }>(
      `/stores/${id}/antigaspi/${page}`,
    )
    return { data: response.data.products, hasMorePage: response.data.hasMorePage }
  }
  const response = await gozokiApi.get<{ products: Product[]; hasMorePage: boolean }>(
    `/stores/${id}/antigaspi`,
  )
  return { data: response.data.products, hasMorePage: response.data.hasMorePage }
}

export const fetchStoreCategoryProducts = async (id: number, cat: number, page?: number) => {
  if (page) {
    const response = await gozokiApi.get<{ products: Product[]; hasMorePage: boolean }>(
      `/stores/${id}/category-products/${cat}/${page}`,
    )
    return { data: response.data.products, hasMorePage: response.data.hasMorePage }
  }
  const response = await gozokiApi.get<{ products: Product[]; hasMorePage: boolean }>(
    `/stores/${id}/category-products/${cat}`,
  )
  return { data: response.data.products, hasMorePage: response.data.hasMorePage }
}

export const fetchPromoProducts = async (id: number, page?: number) => {
  if (page) {
    const response = await gozokiApi.get<{
      promoProducts: PromoProduct[]
      hasMorePage: boolean
    }>(`/stores/${id}/promo-products/${page}`)
    return { data: response.data.promoProducts, hasMorePage: response.data.hasMorePage }
  }
  const response = await gozokiApi.get<{ promoProducts: PromoProduct[]; hasMorePage: boolean }>(
    `/stores/${id}/promo-products`,
  )
  return { data: response.data.promoProducts, hasMorePage: response.data.hasMorePage }
}

/** Fetch stock data for a shop */
export const fetchStoreStock = async (id: number) => {
  const response = await gozokiApiWithAuth.get<StockData>(`/stores/${id}/stock`)
  return response.data
}
// Fetch multiples stores stock data
export const fetchStoresStocks = async (ids: number[]) => {
  const data = { ids }
  const response = await gozokiApiWithAuth.get<StockData[]>('/stores/stocks', { params: data })
  return response.data
}

export const fetchStoresmMetaDatas = async (ids: number[]) => {
  const data = { ids }
  const response = await gozokiApiWithAuth.get<StockData[]>('/stores/metadatas', {
    params: data,
  })
  return response.data
}

/** Fetch store articles associated with a product */
export const fetchStoreProductArticles = async (storeId: number, productId: number) => {
  const response = await gozokiApiWithAuth.get<UniqueArticle[]>(
    `/stores/${storeId}/product/${productId}`,
  )
  return response.data
}

/** Get all stores data as a CSV string */
export const exportStoresAsCSV = async () => {
  const response = await gozokiApiWithAuth.get<string>('/stores/export')
  return response.data
}

export const fetchStoreCameraConfig = async (storeId: number) => {
  const response = await gozokiApiWithAuth.get<
    { data: null; runtime: StoreCameraConfigState['runtime'] } | StoreCameraConfigSummary
  >(`/stores/${storeId}/camera-config`)

  if ('data' in response.data) {
    return {
      config: response.data.data,
      runtime: response.data.runtime,
    } satisfies StoreCameraConfigState
  }

  return {
    config: response.data,
    runtime: response.data.runtime,
  } satisfies StoreCameraConfigState
}

export const fetchStoreCameras = async (storeId: number) => {
  const response = await gozokiApiWithAuth.get<StoreCamera[]>(`/stores/${storeId}/cameras`)
  return response.data
}

export const fetchStoreCamera = async (storeId: number, cameraId: number) => {
  const response = await gozokiApiWithAuth.get<StoreCamera>(
    `/stores/${storeId}/cameras/${cameraId}`,
  )
  return response.data
}

export type MinStockValuesDto = Omit<MinStockValues, 'id' | 'createdAt' | 'updatedAt'>

/** Fetch reappro needs for a specific store and date from the database */
export const fetchReapproNeedsByStoreAndDate = async (
  storeReference: string,
  date: string,
): Promise<Record<string, { count: number; product: Product }>> => {
  const response = await gozokiApiWithAuth.get<Record<string, { count: number; product: Product }>>(
    `/stores/reappro-needs/${storeReference}/${date}`,
  )
  return response.data
}

export const exportDeliveryRecapXlsx = async (date?: string) => {
  const response = await gozokiApiWithAuth.get<Blob>('/stores/reappro-needs/delivery-recap-xlsx', {
    params: date ? { date } : undefined,
    responseType: 'blob',
  })
  return response.data
}

export const exportReapproSimulationXlsx = async (storeId: number) => {
  const response = await gozokiApiWithAuth.get<Blob>(
    `/stores/reappro-needs/simulation-xlsx/${storeId}`,
    {
      responseType: 'blob',
    },
  )
  return response.data
}

export const exportReapproSimulationAllStoresXlsx = async () => {
  const response = await gozokiApiWithAuth.get<Blob>(
    '/stores/reappro-needs/simulation-xlsx-all-stores',
    {
      responseType: 'blob',
    },
  )
  return response.data
}

export const requestReapproSimulationAllStoresXlsxByEmail = async (data: {
  emails: string[]
  sendToPilotage: boolean
  referenceDate?: string
}) => {
  const response = await gozokiApiWithAuth.post<{ message: string }>(
    '/stores/reappro-needs/simulation-xlsx-all-stores/email',
    data,
  )

  return response.data
}

export const requestDispatchSimulationAllStoresXlsxByEmail = async (data: {
  emails: string[]
  sendToPilotage: boolean
  referenceDate?: string
}) => {
  const response = await gozokiApiWithAuth.post<{ message: string }>(
    '/stores/reappro-needs/dispatch-simulation-xlsx-all-stores/email',
    data,
  )

  return response.data
}

export const fetchCameraStreams = async (storeId: number): Promise<CameraStream[]> => {
  const response = await gozokiApiWithAuth.get<CameraStream[]>(`/stores/${storeId}/camera-streams`)
  return response.data
}

export const fetchStoreDvrCameras = async (storeId: number): Promise<JvcCamera[]> => {
  const response = await gozokiApiWithAuth.get<{ cameras: JvcCamera[] }>(
    `/stores/${storeId}/camera-streams/dvr-cameras`,
  )
  return response.data.cameras
}

export const fetchStoreActionLogs = async (storeId: number) => {
  const res = await gozokiApiWithAuth.get<StoreActionLog[]>(`/stores/${storeId}/action-logs`)
  return res.data
}
